/**
 * Contacts (Marketing/Leads) page — /contacts.
 * Three tabs: All Leads (server-paginated list) · My Leads · Statistics.
 * Stat band from GET /contacts/statistic across the top.
 * Active tab persisted in ?tab= search param.
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { useContacts, useMyLeads } from '@/api/contacts';
import { toFilters } from '@/api/customers';
import type { ContractListParams } from '@/api/contracts';
import { ContractListSection } from '@/pages/contracts/components/contract-list-section';
import { ContactStatBand } from './contact-stat-band';
import { ContactStatisticsTab } from './contact-statistics-tab';
import { contactColumns } from './contact-column-definitions';
import type { FilterField } from '@vnetwork/ui/data';
import type { ContactRow } from '@/api/contacts';

const TAB_VALUES = ['all', 'my-leads', 'statistics'] as const;
type TabValue = (typeof TAB_VALUES)[number];

/* ── Filter fields ─────────────────────────────────────────────────────── */

const CONTACT_FILTER_FIELDS: FilterField[] = [
  {
    value: 'contact_source_status',
    label: 'Status',
    input: 'select',
    options: [
      { value: 'UNCLASSIFIED', label: 'Unclassified' },
      { value: 'warm-lead', label: 'Warm Lead' },
      { value: 'sale-qualified-lead', label: 'SQL' },
      { value: 'cool-lead', label: 'Cool Lead' },
      { value: 'un-qualified-lead', label: 'Unqualified' },
    ],
  },
  {
    value: 'contact_source_channel',
    label: 'Channel',
    input: 'select',
    options: [
      { value: 'form', label: 'Form' },
      { value: 'livechat', label: 'Live Chat' },
      { value: 'fb_messenger', label: 'FB Messenger' },
      { value: 'fb_ads', label: 'FB Ads' },
      { value: 'gg_ads', label: 'Google Ads' },
      { value: 'hotline', label: 'Hotline' },
      { value: 'event', label: 'Event' },
      { value: 'email', label: 'Email' },
    ],
  },
];

/* ── Query adapters ─────────────────────────────────────────────────────
 * ContractListSection passes filters as Record<string,unknown> (the raw
 * conditionsToFilters output). The contacts API expects the same base64 JSON
 * string that the customers API uses. These thin wrappers encode on the way in.
 * ─────────────────────────────────────────────────────────────────────────── */

function useAllContactsAdapted(params: ContractListParams) {
  const filters = params.filters ? toFilters(params.filters) : undefined;
  return useContacts({ page: params.page, page_size: params.page_size, q: params.q, filters });
}

function useMyLeadsAdapted(params: ContractListParams) {
  const filters = params.filters ? toFilters(params.filters) : undefined;
  return useMyLeads({ page: params.page, page_size: params.page_size, q: params.q, filters });
}

/* ── Page ──────────────────────────────────────────────────────────────── */

export default function ContactsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab') as TabValue | null;
  const activeTab: TabValue =
    requestedTab && (TAB_VALUES as readonly string[]).includes(requestedTab)
      ? requestedTab
      : 'all';

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const onRowClick = (row: ContactRow) => navigate(`/contacts/${row.contact_source_id}`);

  return (
    <Container size="full">
      <PageHeader
        title="Contacts"
        description="Marketing leads pipeline — all inbound contacts, their classification status, and revenue attribution."
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      <ContactStatBand />

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="all">All Leads</TabsTrigger>
          <TabsTrigger value="my-leads">My Leads</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {activeTab === 'all' && (
            <ContractListSection
              useListQuery={useAllContactsAdapted}
              columns={contactColumns}
              getRowId={(r) => String(r.contact_source_id)}
              onRowClick={onRowClick}
              filterFields={CONTACT_FILTER_FIELDS}
              searchPlaceholder="Search contacts…"
              errorTitle="Failed to load contacts"
              emptyTitle="No contacts found"
              emptyDescription="Try adjusting your search or filters."
            />
          )}
        </TabsContent>

        <TabsContent value="my-leads">
          {activeTab === 'my-leads' && (
            <ContractListSection
              useListQuery={useMyLeadsAdapted}
              columns={contactColumns}
              getRowId={(r) => String(r.contact_source_id)}
              onRowClick={onRowClick}
              searchPlaceholder="Search my leads…"
              errorTitle="Failed to load my leads"
              emptyTitle="No leads assigned to you"
              emptyDescription="Contacts assigned to you will appear here."
            />
          )}
        </TabsContent>

        <TabsContent value="statistics">
          {activeTab === 'statistics' && <ContactStatisticsTab />}
        </TabsContent>
      </Tabs>
    </Container>
  );
}
